import { forwardRef, useMemo, type Ref } from "react"
import {
  useLocation,
  Link as RLink,
  type LinkProps as RLinkProps,
} from "react-router-dom"
import { cva, type VariantProps } from "class-variance-authority"
import cn from "@/v2-ui/utils/utils.cn"
import Icon, { type IconProps } from "@/v2-ui/icon"
import { isPartialHref, resolvePartialHref } from "@/v2-backoffice-client/utils"

const vars = cva("ui-link", {
  variants: {
    variant: {
      default: "ui-link-default",
      muted: "ui-link-muted",
      ghost: "ui-link-ghost",
      unstyled: "",
    },
    size: {
      xs: "ui-link-xs",
      sm: "ui-link-sm",
      md: "ui-link-md",
      lg: "ui-link-lg",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "sm",
  },
})

export type LinkProps = Omit<RLinkProps, "className"> &
  VariantProps<typeof vars> & {
    className?: string
    external?: boolean
    iconSrc?: IconProps["src"] | null
    iconSize?: IconProps["size"]
    iconClassName?: IconProps["className"]
    iconStyle?: IconProps["style"]
  }

function Link(props: LinkProps, ref: Ref<HTMLAnchorElement>) {
  const {
    variant,
    size,
    className,
    to,
    iconSrc,
    iconSize = 8,
    iconClassName,
    iconStyle,
    children,
    ...linkProps
  } = props

  // `unstyled` opts out of the whole class system - base, variant and size -
  // not just the variant. Callers reach for it precisely when they bring
  // their own styling (a link drawn as a button, a link inline in a
  // sentence), and `ui-link` carries layout of its own (inline-flex, gap,
  // text size) that then has to be fought back.
  const variantClasses = variant === "unstyled" ? null : vars({ variant, size })

  const isViewSheetPush = typeof to === "string" && to.startsWith(">>?")

  const location = useLocation()
  const resolvedTo = useMemo(() => {
    if (typeof to !== "string" || !isPartialHref(to)) return to
    return resolvePartialHref(to, location)
  }, [to, location])

  if (linkProps.external === undefined) {
    linkProps.external = String(resolvedTo).includes("//")
  }

  if (linkProps.external) {
    const _iconSrc = iconSrc === null ? null : "arrow-up-right-from-square"
    delete linkProps.external

    return (
      <a
        ref={ref}
        href={String(resolvedTo)}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(variantClasses, className)}
        {...(linkProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
        {_iconSrc && (
          <Icon
            className={cn(iconClassName, "self-start")}
            src={_iconSrc}
            size={iconSize}
            style={iconStyle}
          />
        )}
      </a>
    )
  }

  const _iconSrc = iconSrc === null ? null : "link"
  delete linkProps.external

  return (
    <RLink
      ref={ref}
      to={resolvedTo}
      state={isViewSheetPush ? { vshistory: "push" } : undefined}
      className={cn(variantClasses, className)}
      {...linkProps}
    >
      {children}
      {_iconSrc && (
        <Icon
          className={cn(iconClassName, "self-start")}
          src={_iconSrc}
          size={iconSize}
          style={iconStyle}
        />
      )}
    </RLink>
  )
}

export default forwardRef(Link)
